import { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  keyframes,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import QuizRoundedIcon from "@mui/icons-material/QuizRounded";
import StyleRoundedIcon from "@mui/icons-material/StyleRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import parse from "html-react-parser";
import MDButton from "components/MDButton";
import {
  STATIC_COPILOT_COSTS,
  sendCopilotChatCall,
} from "../../../redux/api/copilot.api";

/*
  CopilotChatPanel
  ──────────────────────────────────────────────────────────────
  The "Chat" tab inside a Study Pack. Drives every intent
  exposed by POST /lecture-lab/materials/:id/copilot-chat:

    • chat_answer         — free-form Q&A grounded on the lecture
    • extra_quizzes       — append more MCQs to the Quiz tab
    • extra_flashcards    — append more flashcards to Flashcards tab
    • deepen_study_chart  — re-run the topics graph with AI

  Each call costs 1 credit regardless of intent. The server
  appends both user + assistant messages into the study pack's
  `copilotMessages` JSONB and returns the updated pack — the
  parent screen merges that back so the Quiz / Flashcards /
  Topics tabs update live.

  Intent chips work like the mobile composer:
    • Tap an intent that requires a prompt → prefill default,
      let the user edit, send when they're happy.
    • Tap an intent that doesn't require a prompt (deepen
      chart) → fire immediately, no composer interaction.
*/

const INTENTS = [
  {
    key: "chat_answer",
    label: "Ask",
    icon: <ChatRoundedIcon fontSize="small" />,
    placeholder: "What does the lecture say about…",
    defaultPrompt: "",
    requiresPrompt: true,
    color: "primary",
    description: "Ask a free-form question about this lecture.",
  },
  {
    key: "extra_quizzes",
    label: "More quizzes",
    icon: <QuizRoundedIcon fontSize="small" />,
    placeholder: "Focus on… (optional)",
    defaultPrompt: "Generate 5 more multiple choice questions covering different topics from the lecture.",
    requiresPrompt: false,
    color: "info",
    description: "Add 5 more MCQs to the Quiz tab.",
  },
  {
    key: "extra_flashcards",
    label: "More flashcards",
    icon: <StyleRoundedIcon fontSize="small" />,
    placeholder: "Focus on… (optional)",
    defaultPrompt: "Generate 5 more front/back flashcards covering different concepts from the lecture.",
    requiresPrompt: false,
    color: "success",
    description: "Add 5 more flashcards to the Flashcards tab.",
  },
  {
    key: "deepen_study_chart",
    label: "Deepen topics",
    icon: <AccountTreeRoundedIcon fontSize="small" />,
    placeholder: "",
    defaultPrompt: "",
    requiresPrompt: false,
    color: "warning",
    description: "Re-run the topics graph with AI (1 credit).",
  },
];

// Soft conic-gradient rotation, used as the composer's
// "thinking" border halo. Cheap on web — purely a background
// animation, no layout impact.
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function renderHtml(html) {
  return (
    <Box
      sx={{
        fontSize: 15,
        lineHeight: 1.65,
        "& p": { margin: 0, mb: 0.8 },
        "& p:last-of-type": { mb: 0 },
        "& ul, & ol": { margin: "6px 0 0", pl: 2.5 },
        "& li": { marginBottom: 0.4 },
        "& strong, & b": { fontWeight: 700 },
      }}
    >
      {parse(html || "")}
    </Box>
  );
}

export default function CopilotChatPanel({
  materialId,
  initialMessages = [],
  credits,
  onStudyPackUpdate,
  onCreditsChange,
  onInsufficientCredits,
  onNavigateToTab,
}) {
  const [messages, setMessages] = useState(initialMessages);
  const [activeIntent, setActiveIntent] = useState("chat_answer");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const transcriptRef = useRef(null);
  const composerCost = credits?.costs?.copilot_chat ?? STATIC_COPILOT_COSTS.copilot_chat;

  // Keep the panel state in sync when the parent reloads the
  // study pack (e.g. on tab focus). We intentionally use
  // `length + last id` as the comparison signal so retyping the
  // same messages array reference doesn't blow away local state.
  useEffect(() => {
    setMessages(initialMessages || []);
  }, [initialMessages?.length, initialMessages?.[initialMessages?.length - 1]?.id]);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const intent = INTENTS.find((i) => i.key === activeIntent) || INTENTS[0];

  const onIntentClick = (k) => {
    const next = INTENTS.find((i) => i.key === k) || INTENTS[0];
    setActiveIntent(k);
    // For "deepen topics" — no prompt needed, fire immediately.
    if (!next.requiresPrompt && k === "deepen_study_chart") {
      void send(k, "");
      return;
    }
    setInput(next.defaultPrompt || "");
  };

  const canSend = !sending && (intent.requiresPrompt ? !!input.trim() : true);

  const send = async (intentKey, body) => {
    if (sending) return;
    const useIntent = intentKey || activeIntent;
    const useMessage = (body ?? input).trim();
    if (intent.requiresPrompt && !useMessage) return;

    if (credits && Number(credits.creditsRemaining) < composerCost) {
      onInsufficientCredits?.({ required: composerCost, remaining: credits.creditsRemaining });
      return;
    }

    setSending(true);
    const optimistic = {
      id: `tmp-user-${Date.now()}`,
      role: "user",
      intent: useIntent,
      text: useMessage || `(${INTENTS.find((i) => i.key === useIntent)?.label || "request"})`,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    const previousInput = input;
    if (useIntent === activeIntent) setInput("");

    try {
      const res = await sendCopilotChatCall({
        materialId,
        message: useMessage,
        intent: useIntent,
      });

      if (res?.status === 402) {
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setInput(previousInput);
        onInsufficientCredits?.({
          required: res?.credits?.required ?? composerCost,
          remaining: res?.credits?.creditsRemaining ?? 0,
        });
        return;
      }

      if (res?.status !== 200) {
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setInput(previousInput);
        window.alert(res?.message || "Microlab Copilot couldn't process that — please retry.");
        return;
      }

      // Server returns the authoritative user + assistant
      // messages — swap the optimistic bubble for the real one
      // so id / createdAt match what the server stored.
      if (res.userMessage && res.assistantMessage) {
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== optimistic.id),
          res.userMessage,
          res.assistantMessage,
        ]);
      }
      if (res.studyPack && typeof onStudyPackUpdate === "function") {
        onStudyPackUpdate(res.studyPack);
      }
      if (res.credits && typeof onCreditsChange === "function") {
        onCreditsChange(res.credits);
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setInput(previousInput);
      window.alert("Failed to reach Microlab Copilot. Please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack
      spacing={1.5}
      sx={{
        height: { xs: "auto", md: "min(72vh, 720px)" },
        minHeight: 480,
      }}
    >
      {/* Intent chips */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
        {INTENTS.map((i) => {
          const isActive = i.key === activeIntent;
          return (
            <Chip
              key={i.key}
              icon={i.icon}
              label={i.label}
              onClick={() => onIntentClick(i.key)}
              color={i.color}
              variant={isActive ? "filled" : "outlined"}
              sx={{ fontWeight: 700 }}
            />
          );
        })}
      </Stack>

      {/* Transcript */}
      <Paper
        variant="outlined"
        ref={transcriptRef}
        sx={(t) => ({
          flex: 1,
          overflowY: "auto",
          p: { xs: 1.4, md: 2 },
          borderRadius: 3,
          backgroundColor:
            t.palette.mode === "dark"
              ? alpha(t.palette.primary.main, 0.02)
              : "#fafbfc",
        })}
      >
        {messages.length === 0 ? (
          <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ py: 4 }}>
            <Box
              sx={(t) => ({
                width: 64,
                height: 64,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                backgroundImage: `linear-gradient(135deg, ${t.palette.info.main}, ${t.palette.primary.main})`,
              })}
            >
              <AutoAwesomeRoundedIcon />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Chat with your study pack
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
              Ask a question, request more quizzes / flashcards, or rebuild the topics chart. Every send
              costs 1 credit.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" sx={{ rowGap: 1 }}>
              {INTENTS.map((i) => (
                <Chip
                  key={`s-${i.key}`}
                  icon={i.icon}
                  label={i.description}
                  variant="outlined"
                  onClick={() => onIntentClick(i.key)}
                  sx={{ maxWidth: 320 }}
                />
              ))}
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1.2}>
            {messages.map((m) => {
              const isUser = m.role === "user";
              return (
                <Box
                  key={m.id}
                  sx={{
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={(t) => ({
                      maxWidth: "88%",
                      p: 1.5,
                      borderRadius: 2.5,
                      backgroundColor: isUser
                        ? t.palette.info.main
                        : t.palette.mode === "dark"
                          ? alpha("#fff", 0.06)
                          : "#fff",
                      color: isUser ? "#fff" : t.palette.text.primary,
                      border: isUser ? "none" : `1px solid ${t.palette.divider}`,
                      boxShadow: isUser
                        ? `0 6px 18px ${alpha(t.palette.info.main, 0.25)}`
                        : "0 1px 2px rgba(15,23,42,.06)",
                    })}
                  >
                    {isUser ? (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {m.text}
                      </Typography>
                    ) : (
                      renderHtml(m.text || "")
                    )}
                    {m.generatedKind && !isUser ? (
                      <Stack direction="row" spacing={1} mt={1}>
                        {m.generatedKind === "quiz_questions" ? (
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={() => onNavigateToTab?.("quiz")}
                            startIcon={<QuizRoundedIcon fontSize="small" />}
                          >
                            Open quiz tab
                          </MDButton>
                        ) : null}
                        {m.generatedKind === "flashcards" ? (
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => onNavigateToTab?.("flashcards")}
                            startIcon={<StyleRoundedIcon fontSize="small" />}
                          >
                            Open flashcards
                          </MDButton>
                        ) : null}
                        {m.generatedKind === "study_chart" ? (
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => onNavigateToTab?.("topics")}
                            startIcon={<AccountTreeRoundedIcon fontSize="small" />}
                          >
                            Open topics
                          </MDButton>
                        ) : null}
                      </Stack>
                    ) : null}
                  </Paper>
                </Box>
              );
            })}
            {sending ? (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Paper
                  elevation={0}
                  sx={(t) => ({
                    p: 1.4,
                    borderRadius: 2.5,
                    border: `1px solid ${t.palette.divider}`,
                    backgroundColor: t.palette.mode === "dark" ? alpha("#fff", 0.05) : "#fff",
                  })}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      Microlab Copilot is working on it…
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            ) : null}
          </Stack>
        )}
      </Paper>

      {/* Composer */}
      <Box
        sx={(t) => ({
          position: "relative",
          borderRadius: 3,
          border: `1px solid ${t.palette.divider}`,
          p: 1.2,
          backgroundColor: t.palette.background.paper,
          overflow: "hidden",
          // While sending, a rotating conic-gradient peeks
          // around the border as a "thinking" affordance.
          "&::before": sending
            ? {
                content: '""',
                position: "absolute",
                inset: -2,
                borderRadius: "inherit",
                backgroundImage: `conic-gradient(from 0deg, ${t.palette.info.main}, ${t.palette.primary.main}, ${t.palette.warning.main}, ${t.palette.info.main})`,
                opacity: 0.7,
                zIndex: 0,
                animation: `${rotate} 2.4s linear infinite`,
              }
            : undefined,
          "& > *": { position: "relative", zIndex: 1 },
          "&::after": sending
            ? {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                backgroundColor: t.palette.background.paper,
                zIndex: 0,
              }
            : undefined,
        })}
      >
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={5}
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, 600))}
            placeholder={intent.placeholder || "Type a question…"}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            size="small"
            disabled={sending}
          />
          <Tooltip arrow title={`Send (1 credit)`}>
            <span>
              <MDButton
                variant="gradient"
                color="info"
                disabled={!canSend}
                onClick={() => send()}
                sx={{ minWidth: 64 }}
              >
                {sending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : intent.key === "deepen_study_chart" ? (
                  <RocketLaunchRoundedIcon fontSize="small" />
                ) : (
                  <SendRoundedIcon fontSize="small" />
                )}
              </MDButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5, px: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {intent.description} · costs {composerCost} credit{composerCost === 1 ? "" : "s"}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {credits ? `${credits.creditsRemaining} credits left` : ""}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}
